import {
  CONTENT_ID_ASSETS_LOADED,
  CONTENT_ID_ASSETS_CLEAR,
  CONTENT_ID_ASSET_DETAILS_LOADED,
  CONTENT_ID_ASSET_DETAILS_CLEAR,
  CONTENT_ID_ASSET_MATCH_POLICY_LOADED,
  CONTENT_ID_ASSET_MATCH_POLICY_CLEAR,
  CONTENT_ID_BULK_ASSET_REQUESTS_LOADED,
  CONTENT_ID_BULK_ASSET_REQUESTS_CLEAR,
  CONTENT_ID_BULK_ASSET_REQUEST_STATUS_LOADED,
  CONTENT_ID_BULK_ASSET_REQUEST_STATUS_CLEAR,
} from '../../actionTypes';

import * as api from '../../apiHelper';
import { arriveNotice } from '../../noticeActions';
import {
  setFetchLoaderLoading,
  setFetchLoaderReset,
  setSecondaryFetchLoaderLoading,
  setSecondaryFetchLoaderReset,
  setSubmissionLoaderLoading,
  setSubmissionLoaderReset,
} from '../../fetchLoaderActions';
import { setFaviconLoading, setFaviconDefault } from '../../faviconActions';

// Reducer corresponding actions
function contentIdAssetsClear() {
  return { type: CONTENT_ID_ASSETS_CLEAR };
}

function contentIdAssetsLoaded(assets) {
  return {
    type: CONTENT_ID_ASSETS_LOADED,
    payload: { assets },
  };
}

function contentIdAssetDetailsClear() {
  return { type: CONTENT_ID_ASSET_DETAILS_CLEAR };
}

function contentIdAssetDetailsLoaded(assetDetails) {
  return {
    type: CONTENT_ID_ASSET_DETAILS_LOADED,
    payload: { assetDetails },
  };
}

function contentIdAssetMatchPolicyClear() {
  return { type: CONTENT_ID_ASSET_MATCH_POLICY_CLEAR };
}

function contentIdAssetMatchPolicyLoaded(matchPolicy) {
  return {
    type: CONTENT_ID_ASSET_MATCH_POLICY_LOADED,
    payload: { matchPolicy },
  };
}

function bulkAssetRequestsLoaded(bulkAssetRequests) {
  return {
    type: CONTENT_ID_BULK_ASSET_REQUESTS_LOADED,
    payload: { bulkAssetRequests },
  };
}

function bulkAssetRequestsClear() {
  return { type: CONTENT_ID_BULK_ASSET_REQUESTS_CLEAR };
}

function bulkAssetRequestStatusLoaded(bulkAssetRequestStatus) {
  return {
    type: CONTENT_ID_BULK_ASSET_REQUEST_STATUS_LOADED,
    payload: { bulkAssetRequestStatus },
  };
}

function bulkAssetRequestStatusClear() {
  return { type: CONTENT_ID_BULK_ASSET_REQUEST_STATUS_CLEAR };
}

// API
async function getContentIdAssetsApi(token, params, isExport = false) {
  if (isExport) {
    await api.getExport({
      path: 'youtube-cid/assets/export',
      token,
      params,
      version: 'gateway',
      filename: 'content-id-assets-export',
    });
    return null;
  }

  const response = await api.get({
    path: 'youtube-cid/assets',
    token,
    params,
    version: 'gateway',
  });
  return response.json();
}

async function getContentIdAssetDetailsApi(token, assetId) {
  const response = await api.get({
    path: `youtube-cid/assets/${assetId}`,
    token,
    version: 'gateway',
  });
  return response.json();
}

async function updateContentIdAssetDetailsApi(token, assetId, body) {
  const response = await api.put({
    path: `youtube-cid/assets/${assetId}`,
    token,
    body,
    version: 'gateway',
  });
  return response.json();
}

async function getContentIdAssetMatchPolicyApi(token, assetId) {
  const response = await api.get({
    path: `youtube-cid/assets/${assetId}/match-policy`,
    token,
    version: 'gateway',
  });
  return response.json();
}

async function updateContentIdAssetMatchPolicyApi(token, assetId, body) {
  const response = await api.patch({
    path: `youtube-cid/assets/${assetId}/match-policy`,
    token,
    body,
    version: 'gateway',
  });
  return response.json();
}

async function postBulkAssetRequestApi(token, body) {
  const response = await api.post({
    path: 'youtube-cid/assets/bulk-requests',
    token,
    body,
    version: 'gateway',
  });
  return response.json();
}

async function getBulkAssetRequestsApi(token, params) {
  const response = await api.get({
    path: 'youtube-cid/assets/bulk-requests',
    token,
    params,
    version: 'gateway',
  });
  return response.json();
}

async function getBulkAssetRequestStatusApi(token, requestId) {
  const response = await api.get({
    path: `youtube-cid/assets/bulk-requests/${requestId}`,
    token,
    version: 'gateway',
  });
  return response.json();
}

// Helper
async function fetchContentIdAssets(dispatch, params, token, isExport = false) {
  try {
    const assets = await getContentIdAssetsApi(token, params, isExport);
    if (!isExport) {
      dispatch(setFaviconDefault());
      dispatch(setFetchLoaderReset());
      dispatch(contentIdAssetsLoaded(assets));
    }

    if (isExport) dispatch(setSubmissionLoaderReset());
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(arriveNotice(e.message));
    }
  } finally {
    dispatch(setFaviconDefault());
    dispatch(setFetchLoaderReset());
    if (isExport) dispatch(setSubmissionLoaderReset());
  }
}

async function fetchContentIdAssetDetails(dispatch, assetId, token) {
  try {
    const assetDetails = await getContentIdAssetDetailsApi(token, assetId);
    dispatch(contentIdAssetDetailsLoaded(assetDetails));
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(arriveNotice(e.message));
    }
  } finally {
    dispatch(setFaviconDefault());
    dispatch(setFetchLoaderReset());
  }
}

async function updateContentIdAssetDetails(dispatch, assetId, token, body) {
  try {
    await updateContentIdAssetDetailsApi(token, assetId, body);
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(arriveNotice(e.message));
    }
  } finally {
    dispatch(setSubmissionLoaderReset());
  }
}

async function fetchContentIdAssetMatchPolicy(dispatch, assetId, token) {
  try {
    const matchPolicy = await getContentIdAssetMatchPolicyApi(token, assetId);
    dispatch(contentIdAssetMatchPolicyLoaded(matchPolicy));
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(arriveNotice(e.message));
    }
  } finally {
    dispatch(setFaviconDefault());
    dispatch(setSecondaryFetchLoaderReset());
  }
}

async function updateContentIdAssetMatchPolicy(dispatch, assetId, token, body) {
  try {
    const matchPolicy = await updateContentIdAssetMatchPolicyApi(token, assetId, body);
    dispatch(contentIdAssetMatchPolicyLoaded(matchPolicy));
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(arriveNotice(e.message));
    }
  } finally {
    dispatch(setSubmissionLoaderReset());
  }
}

async function fetchBulkAssetRequests(dispatch, params, token) {
  try {
    const bulkAssetRequests = await getBulkAssetRequestsApi(token, params);
    dispatch(bulkAssetRequestsLoaded(bulkAssetRequests));
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(arriveNotice(e.message));
    }
  } finally {
    dispatch(setFaviconDefault());
    dispatch(setFetchLoaderReset());
  }
}

async function fetchBulkAssetRequestStatus(dispatch, requestId, token) {
  try {
    const bulkAssetRequestStatus = await getBulkAssetRequestStatusApi(token, requestId);
    dispatch(bulkAssetRequestStatusLoaded(bulkAssetRequestStatus));
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(arriveNotice(e.message));
    }
  } finally {
    dispatch(setFaviconDefault());
    dispatch(setSecondaryFetchLoaderReset());
  }
}

async function createBulkAssetRequest(dispatch, token, body) {
  try {
    const response = await postBulkAssetRequestApi(token, body);
    dispatch(arriveNotice(response.message, 'ok'));
    return response;
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(arriveNotice(e.message));
    }
    return null;
  } finally {
    dispatch(setSubmissionLoaderReset());
  }
}

// Public components actions
export function initiateFetchAssetsRequest(params, isExport = false) {
  return async (dispatch, getState) => {
    const {
      auth: { token, paymentPending },
      favicon: { faviconStatus },
      fetchLoader: { dataLoading, submissionLoading },

    } = getState();

    // Prevent API call if payment is pending
    if (paymentPending) return;

    if (!dataLoading && !isExport) {
      dispatch(setFetchLoaderLoading());
    } else if (!submissionLoading && isExport) {
      dispatch(setSubmissionLoaderLoading());
    }

    if (faviconStatus !== 'loading' && !isExport) {
      dispatch(setFaviconLoading());
    }

    await fetchContentIdAssets(dispatch, params, token, isExport);
  };
}

export function clearContentIdAssetsRequest() {
  return (dispatch) => {
    dispatch(contentIdAssetsClear());
  };
}

export function initiateFetchAssetDetailsRequest(assetId) {
  return async (dispatch, getState) => {
    const {
      auth: { token },
      favicon: { faviconStatus },
      fetchLoader: { dataLoading },
    } = getState();

    if (!dataLoading) {
      dispatch(setFetchLoaderLoading());
    }

    if (faviconStatus !== 'loading') {
      dispatch(setFaviconLoading());
    }

    await fetchContentIdAssetDetails(dispatch, assetId, token);
  };
}

export function clearContentIdAssetDetailsRequest() {
  return (dispatch) => {
    dispatch(contentIdAssetDetailsClear());
  };
}

export function updateContentIdAssetDetailsRequest(assetId, body) {
  return async (dispatch, getState) => {
    const {
      auth: { token },
      fetchLoader: { submissionLoading },
    } = getState();

    if (!submissionLoading) {
      dispatch(setSubmissionLoaderLoading());
    }

    await updateContentIdAssetDetails(dispatch, assetId, token, body);
  };
}

export function initiateFetchAssetMatchPolicyRequest(assetId) {
  return async (dispatch, getState) => {
    const {
      auth: { token },
      favicon: { faviconStatus },
      fetchLoader: { secondaryDataLoading },
    } = getState();

    if (!secondaryDataLoading) {
      dispatch(setSecondaryFetchLoaderLoading());
    }

    if (faviconStatus !== 'loading') {
      dispatch(setFaviconLoading());
    }

    await fetchContentIdAssetMatchPolicy(dispatch, assetId, token);
  };
}

export function clearContentIdAssetMatchPolicyRequest() {
  return (dispatch) => {
    dispatch(contentIdAssetMatchPolicyClear());
  };
}

export function updateContentIdAssetMatchPolicyRequest(assetId, body) {
  return async (dispatch, getState) => {
    const {
      auth: { token },
      fetchLoader: { submissionLoading },
    } = getState();

    if (!submissionLoading) {
      dispatch(setSubmissionLoaderLoading());
    }

    await updateContentIdAssetMatchPolicy(dispatch, assetId, token, body);
  };
}

// Bulk asset actions
export function initiateCreateBulkAssetRequest(body) {
  return async (dispatch, getState) => {
    const {
      auth: { token },
      fetchLoader: { submissionLoading },
    } = getState();

    if (!submissionLoading) {
      dispatch(setSubmissionLoaderLoading());
    }

    const response = await createBulkAssetRequest(dispatch, token, body);
    return response;
  };
}

export function initiateFetchBulkAssetRequest(params) {
  return async (dispatch, getState) => {
    const {
      auth: { token },
      favicon: { faviconStatus },
      fetchLoader: { dataLoading },
    } = getState();

    if (!dataLoading) {
      dispatch(setFetchLoaderLoading());
    }
    if (faviconStatus !== 'loading') {
      dispatch(setFaviconLoading());
    }

    await fetchBulkAssetRequests(dispatch, params, token);
  };
}

export function clearBulkAssetRequest() {
  return (dispatch) => {
    dispatch(bulkAssetRequestsClear());
  };
}

export function initiateFetchBulkAssetStatusRequest(requestId) {
  return async (dispatch, getState) => {
    const {
      auth: { token },
      favicon: { faviconStatus },
      fetchLoader: { secondaryDataLoading },
    } = getState();

    if (!secondaryDataLoading) {
      dispatch(setSecondaryFetchLoaderLoading());
    }
    if (faviconStatus !== 'loading') {
      dispatch(setFaviconLoading());
    }

    await fetchBulkAssetRequestStatus(dispatch, requestId, token);
  };
}

export function clearBulkAssetStatusRequest() {
  return (dispatch) => {
    dispatch(bulkAssetRequestStatusClear());
  };
}

